/* RAVIN / THE FOLD — true 3D Scroll Deck -> true Sideways Deck.
   The incoming card is NEVER duplicated. The ACTUAL Sideways gallery
   overlaps beneath the Scroll Deck; the real outgoing last card folds
   away and exposes the real next card through a horizontal paper slit. */
(() => {
  if (window.__RavinLabFoldBooted) return;
  window.__RavinLabFoldBooted = true;

  const instances=new WeakMap();
  const selector='[data-ravin-3d-deck][data-ravin-fold-enabled]';
  const clamp=(x,min,max)=>Math.max(min,Math.min(max,x));
  const ease=x=>{const v=clamp(x,0,1);return v*v*(3-2*v);};
  const ramp=(x,start,end)=>ease((x-start)/Math.max(.001,end-start));
  const mobile=window.matchMedia('(max-width:749px)');
  const reduced=window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    if(!root||instances.has(root))return;
    const stage=root.querySelector('[data-ravin-deck-stage]');
    const overlay=root.querySelector('[data-ravin-fold]');
    const outgoing=Array.from(root.querySelectorAll('[data-ravin-deck-card]')).at(-1);
    const shell=root.closest('.shopify-section');
    if(!stage||!overlay||!outgoing||!shell)return;

    const controller=new AbortController();
    const desktopOriginal=Number(root.dataset.ravinFoldOriginalDesktop)||100;
    const mobileOriginal=Number(root.dataset.ravinFoldOriginalMobile)||100;
    const desktopTail=Number(root.dataset.ravinFoldDesktopVh)||110;
    const mobileTail=Number(root.dataset.ravinFoldMobileVh)||80;
    let sideways=null;
    let sidewaysShell=null;
    let ready=false;
    let wasFolding=false;
    let raf=0;
    let visible=true;
    let tail=0;

    function svh(){
      const probe=document.createElement('span');
      probe.style.cssText='position:absolute;top:0;left:0;width:0;height:100svh;visibility:hidden;pointer-events:none;';
      stage.appendChild(probe);
      const h=probe.getBoundingClientRect().height;
      probe.remove();
      return Math.max(1,h||window.innerHeight);
    }
    function tailPixels(){return svh()*(mobile.matches?mobileTail:desktopTail)/100;}

    function targetSideways(){
      // Only the immediately following actual Shopify section can be folded in.
      return shell.nextElementSibling?.querySelector('[data-ravin-3d-sideways-deck]')||null;
    }

    function sourceGeometry(on){
      const d=desktopOriginal+(on?desktopTail:0);
      const m=mobileOriginal+(on?mobileTail:0);
      root.style.setProperty('--ravin-deck-desktop-height',d+'svh');
      root.style.setProperty('--ravin-deck-mobile-height',m+'svh');
      root.dataset.ravinFoldReady=String(on);
      tail=on?tailPixels():0;
      root.dataset.ravinFoldTailPx=String(tail);
    }

    function targetGeometry(){
      if(!ready||!sidewaysShell)return;
      // At the exact start of The Fold the Sideways real section reaches
      // viewport top; the sticky Sideways stage is held while the fold runs.
      const overlap=window.innerHeight+tail;
      // One additional editorial viewport of interaction after the Fold;
      // the actual Sideways gallery remains usable for swipe/arrow actions.
      const hold=window.innerHeight*.72;
      sidewaysShell.style.setProperty('--rf-real-overlap',overlap.toFixed(2)+'px');
      sidewaysShell.style.setProperty('--rf-real-hold',hold.toFixed(2)+'px');
    }

    function clearVisuals(){
      delete root.dataset.ravinFoldInFlight;
      // Restore the outgoing section's hit testing when scrolling backward
      // or when Shopify disables/reorders the adjacent Sideways gallery.
      shell.removeAttribute('data-ravin-fold-controls-active');
      for(const prop of [
        '--rf-exit-x','--rf-exit-y','--rf-exit-yaw','--rf-exit-roll','--rf-exit-alpha'
      ]) outgoing.style.removeProperty(prop);
      for(const prop of [
        '--rf-overlay-opacity','--rf-type-opacity','--rf-type-y',
        '--rf-seam-opacity','--rf-seam-edge'
      ]) overlay.style.removeProperty(prop);
      stage.style.removeProperty('-webkit-mask-image');
      stage.style.removeProperty('mask-image');
      stage.style.removeProperty('pointer-events');
      stage.inert=false;
      outgoing.style.removeProperty('pointer-events');
      if(outgoing.classList.contains('is-active'))outgoing.inert=false;
      wasFolding=false;
    }

    function reset(){
      clearVisuals();
      delete shell.dataset.ravinFoldOverlap;
      delete outgoing.dataset.ravinFoldOutgoing;
      if(sidewaysShell){
        delete sidewaysShell.dataset.ravinRealFold;
        sidewaysShell.style.removeProperty('--rf-real-overlap');
        sidewaysShell.style.removeProperty('--rf-real-hold');
      }
      sideways=null;
      sidewaysShell=null;
      ready=false;
      sourceGeometry(false);
      window.dispatchEvent(new Event('scroll'));
    }

    function build() {
      if(root.dataset.ravinFoldEnabled==='false'||reduced.matches) {
        if(ready)reset();
        else sourceGeometry(false);
        return;
      }
      const dest=targetSideways();
      const first=dest?.querySelector('[data-ravin-sideways-card]');
      const destShell=dest?.closest('.shopify-section');
      if(!dest||!first||!destShell){
        if(ready)reset();
        else sourceGeometry(false);
        return;
      }
      if(ready&&sideways===dest&&sidewaysShell===destShell){
        sourceGeometry(true);
        targetGeometry();
        schedule();
        return;
      }
      if(ready)reset();

      sideways=dest;
      sidewaysShell=destShell;
      ready=true;
      outgoing.dataset.ravinFoldOutgoing='true';
      shell.dataset.ravinFoldOverlap='true';
      sidewaysShell.dataset.ravinRealFold='true';
      sourceGeometry(true);
      targetGeometry();
      window.dispatchEvent(new Event('scroll'));
      schedule();
    }

    function paint() {
      raf=0;
      if(!root.isConnected||!visible||!ready)return;
      const rect=root.getBoundingClientRect();
      const viewport=window.innerHeight;
      const tailDistance=Math.max(1,tail);
      // Same original-tail calculation as the real 3D Scroll Deck controller:
      // all five original cards finish BEFORE the extra fold runway starts.
      const originalTravel=Math.max(1,rect.height-viewport-tailDistance);
      const progress=clamp((-rect.top-originalTravel)/tailDistance,0,1);
      if(progress<=.00001){
        if(wasFolding)clearVisuals();
        return;
      }

      root.dataset.ravinFoldInFlight='true';
      wasFolding=true;
      // Real outgoing card can no longer intercept Sideways product links.
      outgoing.inert=true;
      outgoing.style.pointerEvents='none';

      const slit=ramp(progress,.12,.97);
      const left=(50*(1-slit)).toFixed(4)+'%';
      const right=(50+50*slit).toFixed(4)+'%';
      // The mask removes the CENTER of the actual Scroll Deck canvas.
      // There is only one Sideways card: the one in the next live section.
      const mask='linear-gradient(to right,#000 0%,#000 '+left+
        ',transparent '+left+',transparent '+right+
        ',#000 '+right+',#000 100%)';
      stage.style.setProperty('-webkit-mask-image',mask);
      stage.style.setProperty('mask-image',mask);

      const spin=ramp(progress,.08,.82);
      const fade=1-ramp(progress,.60,.94);
      outgoing.style.setProperty('--rf-exit-x',
        (-window.innerWidth*.72*spin).toFixed(2)+'px');
      outgoing.style.setProperty('--rf-exit-y',
        (-viewport*.055*Math.sin(Math.PI*spin)).toFixed(2)+'px');
      outgoing.style.setProperty('--rf-exit-yaw',(-146*spin).toFixed(2)+'deg');
      outgoing.style.setProperty('--rf-exit-roll',(-11*spin).toFixed(2)+'deg');
      outgoing.style.setProperty('--rf-exit-alpha',fade.toFixed(4));

      overlay.style.setProperty('--rf-overlay-opacity',
        (ramp(progress,.01,.13)*(1-ramp(progress,.79,.98))).toFixed(4));
      overlay.style.setProperty('--rf-type-opacity',
        (ramp(progress,.06,.27)*(1-ramp(progress,.50,.82))).toFixed(4));
      overlay.style.setProperty('--rf-type-y',
        (-30*(ramp(progress,.06,.75)-.5)).toFixed(2)+'px');
      overlay.style.setProperty('--rf-seam-opacity',
        (Math.sin(Math.PI*ramp(progress,.03,.88))*.89).toFixed(4));
      overlay.style.setProperty('--rf-seam-edge',right);

      // IMPORTANT: the entire outgoing Shopify section remains stacked ABOVE
      // the real Sideways Deck during the Fold, even after its visual mask
      // opens. Disabling events on the sticky stage alone is insufficient:
      // the transparent section wrapper can still swallow navigation taps.
      // Hand off hit testing to the REAL Sideways arrows as soon as its
      // controls are revealed, without needing another scroll.
      const controlsVisible=progress>=.75;
      shell.toggleAttribute('data-ravin-fold-controls-active',controlsVisible);
      stage.style.pointerEvents=controlsVisible?'none':'';
      stage.inert=controlsVisible;
    }

    function schedule(){if(!raf)raf=window.requestAnimationFrame(paint);}
    function resize(){
      if(ready){sourceGeometry(true);targetGeometry();}
      else sourceGeometry(false);
      schedule();
    }
    window.addEventListener('scroll',schedule,{passive:true,signal:controller.signal});
    window.addEventListener('resize',resize,{passive:true,signal:controller.signal});
    window.addEventListener('pageshow',()=>{build();schedule();},{signal:controller.signal});
    reduced.addEventListener?.('change',()=>{build();schedule();},{signal:controller.signal});
    mobile.addEventListener?.('change',resize,{signal:controller.signal});

    let observer=null;
    if('IntersectionObserver' in window){
      observer=new IntersectionObserver(entries=>{
        visible=entries.some(entry=>entry.isIntersecting);
        if(visible)schedule();
      },{rootMargin:'300px'});
      observer.observe(root);
    }

    const cleanup=()=>{
      controller.abort();
      if(raf)window.cancelAnimationFrame(raf);
      observer?.disconnect();
      reset();
      instances.delete(root);
    };
    cleanup.refresh=build;
    instances.set(root,cleanup);
    window.requestAnimationFrame(()=>{
      if(root.isConnected){build();window.requestAnimationFrame(schedule);}
    });
  }

  function initIn(scope){
    if(scope.matches?.(selector))init(scope);
    scope.querySelectorAll?.(selector).forEach(init);
  }
  function unloadIn(scope){
    const nodes=[];
    if(scope.matches?.(selector))nodes.push(scope);
    scope.querySelectorAll?.(selector).forEach(n=>nodes.push(n));
    nodes.forEach(n=>instances.get(n)?.());
  }
  function refreshAll(){
    document.querySelectorAll(selector).forEach(root=>instances.get(root)?.refresh?.());
  }
  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',()=>initIn(document),{once:true});
  }else initIn(document);
  document.addEventListener('shopify:section:load',e=>{
    initIn(e.target);
    window.requestAnimationFrame(refreshAll);
  });
  document.addEventListener('shopify:section:unload',e=>{
    unloadIn(e.target);
    window.requestAnimationFrame(refreshAll);
  });
})();